/**
 * Module dependencies.
 */

import { Title } from 'app/components/atoms/title/title';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'app/components/atoms/button/button';
import { appRoutes } from 'app/routes';
import { Link } from 'react-router-dom';
import { useProjects } from 'app/hooks/requests/projects/use-projects';
import { Loading } from 'app/components/atoms/loading/loading';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { ChevronRight, Folder, Users } from 'lucide-react';

/**
 * Export `ProjectsOverview` component.
 */

export function ProjectsOverview(): JSX.Element {
  const [translate] = useTranslation();
  const { data, isLoading } = useProjects();
  const projects = data ?? [];

  return (
    <div className={'space-y-5'}>
      <div className={'flex items-baseline justify-between'}>
        <Title>{translate('projects.overview.title')}</Title>

        <Link to={appRoutes.dashboard.projects.addNewProject}>
          <Button type={'primary'}>{translate('projects.overview.createProject')}</Button>
        </Link>
      </div>

      <Loading isLoading={isLoading}>
        {projects.length === 0 ? (
          <div
            className={
              'flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/30 px-6 py-16 text-center'
            }
          >
            <div className={'grid h-12 w-12 place-items-center rounded-full bg-muted text-muted-foreground'}>
              <Folder className={'h-6 w-6'} />
            </div>

            <p className={'mt-4 max-w-[360px] text-sm text-muted-foreground'}>{translate('projects.overview.empty')}</p>
          </div>
        ) : (
          <div className={'grid grid-cols-3 gap-4 max-[991px]:grid-cols-2 max-[600px]:grid-cols-1'}>
            {projects.map(project => (
              <Link
                className={
                  'group rounded-xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md'
                }
                key={project.id}
                to={resolveAppPath(appRoutes.dashboard.projects.project.overview, { projectId: project.id })}
              >
                <div className={'flex items-start justify-between gap-3'}>
                  <span className={'grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary'}>
                    <Folder className={'h-5 w-5'} />
                  </span>

                  <ChevronRight
                    className={'h-4 w-4 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5'}
                  />
                </div>

                <div className={'mt-3 truncate text-base font-semibold text-foreground'}>{project.name}</div>

                <div className={'mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground'}>
                  <span className={'rounded-full bg-muted px-2 py-0.5 font-medium capitalize'}>
                    {project.role ?? 'owner'}
                  </span>

                  {project.isShared && (
                    <span className={'inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 font-medium'}>
                      <Users className={'h-3 w-3'} />

                      {translate('projects.overview.shared')}
                    </span>
                  )}

                  <span>{`IVA ${project.defaultIVA}%`}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Loading>
    </div>
  );
}
